import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Card, CardBody, Col, Row } from 'reactstrap';
import { Role } from 'types';
import { getDevelopmentPlanTemplateName } from 'utils/models/DevelopmentPlan';
import { useOrganizationSettings } from 'utils/util/hooks';
import EmptyState from 'views/Widgets/EmptyState';
import { useAdvancedCurrentPeopleFilter } from 'views/Widgets/Filters/AdvancedSpecializedFilter/context';
import FilterablePeopleTable, {
  TALBE_SIZE_NO_LIMIT,
} from 'views/Widgets/People/FilterablePeopleTable';
import AdminRoleAddOrEditButton from './AdminRoleAddOrEditButton';
import AdminRoleDeleteButton from './AdminRoleDeleteButton';
import { PersonRole, RoleUtil } from './types';
import {
  getCampaignName,
  useOrganizationCampaigns,
} from './useOrganizationCampaigns';

interface Props {
  title: string;
  emptyStateTitle: string;
  values: PersonRole[];
  role: Role;
  onItemChange: (values: PersonRole) => void;
  onItemDelete: (values: PersonRole) => void;
}

const AdminRolesAssignmentTable: FC<Props> = ({
  title,
  emptyStateTitle,
  role,
  values,
  onItemChange,
  onItemDelete,
}) => {
  const intl = useIntl();
  const { formatMessage } = intl;
  const { filterToText } = useAdvancedCurrentPeopleFilter();
  const settings = useOrganizationSettings();

  const {
    editable,
    withTargetAudience,
    withDevelopmentPlanTemplates,
    withTargetCampaigns,
  } = RoleUtil.properties(role);
  const { campaigns } = useOrganizationCampaigns(withTargetCampaigns);

  const columns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.people_administration.administrators.admin_roles_assignment_table.table.column.person.name',
          defaultMessage: 'Person',
        }),
        field: 'person',
        isPerson: true,
        format: 'at',
        className: 'col-auto',
      },
      ...(withTargetAudience
        ? [
            {
              name: formatMessage({
                id: 'app.views.administration.people_administration.administrators.admin_roles_assignment_table.table.column.target_audience.name',
                defaultMessage: 'Target audience',
              }),
              field: 'target_audience',
              sortable: false,
              // leave room for the cycles column when both are shown
              className: withTargetCampaigns ? 'col-4' : 'col-7',
            },
          ]
        : []),

      ...(withDevelopmentPlanTemplates
        ? [
            {
              name: formatMessage({
                id: 'app.views.administration.people_administration.administrators.admin_roles_assignment_table.table.column.target_development_plans.name',
                defaultMessage: 'Target plans',
              }),
              field: 'target_development_plans',
              sortable: false,
              className: 'col-7',
            },
          ]
        : []),
      ...(withTargetCampaigns
        ? [
            {
              name: formatMessage({
                id: 'app.views.administration.people_administration.administrators.admin_roles_assignment_table.table.column.target_campaigns.name',
                defaultMessage: 'Cycles',
              }),
              field: 'target_campaigns',
              sortable: false,
              className: 'col-3',
            },
          ]
        : []),
      {
        name: formatMessage({
          id: 'app.views.administration.people_administration.administrators.admin_roles_assignment_table.table.column.actions.name',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        sortable: false,
        className: 'col-2',
      },
    ],
    [
      formatMessage,
      withTargetAudience,
      withDevelopmentPlanTemplates,
      withTargetCampaigns,
    ]
  );

  const rows = useMemo(
    () =>
      values.map((value) => ({
        ...value,
        target_audience: filterToText(value.config?.target_audience),
        target_development_plans: (value.config?.target_development_plans ?? [])
          .map((template) =>
            getDevelopmentPlanTemplateName(
              template,
              settings.development_plans_templates
            )
          )
          .join(', '),
        // no campaigns configured means every campaign of the organization
        target_campaigns: value.config?.target_campaigns?.length
          ? value.config.target_campaigns
              .map((campaignId) => getCampaignName(campaignId, campaigns))
              .join(', ')
          : formatMessage({
              id: 'app.views.administration.people_administration.administrators.admin_roles_assignment_table.target_campaigns.all_cycles',
              defaultMessage: 'All cycles',
            }),
        actions: (
          <Row className="ps-0 mb-n3 pe-0 flex-nowrap">
            {editable && (
              <Col className="ps-0 col-auto pe-0 pb-3">
                <AdminRoleAddOrEditButton
                  roleName={title}
                  role={role}
                  value={value}
                  callback={onItemChange}
                  buttonText={formatMessage({
                    id: 'app.views.administration.people_administration.administrators.admin_roles_assignment_table.button_text.edit',
                    defaultMessage: 'Edit',
                  })}
                  title={formatMessage(
                    {
                      id: 'app.views.administration.people_administration.administrators.admin_roles_assignment_table.title.edit_rolename',
                      defaultMessage: 'Edit {roleName}',
                    },
                    {
                      roleName: title,
                    }
                  )}
                />
              </Col>
            )}
            <Col className="col-auto ps-0 pe-4 pb-3">
              <AdminRoleDeleteButton value={value} onDelete={onItemDelete} />
            </Col>
          </Row>
        ),
      })),
    [
      values,
      formatMessage,
      filterToText,
      editable,
      title,
      role,
      onItemChange,
      onItemDelete,
      settings.development_plans_templates,
      campaigns,
    ]
  );

  return (
    <>
      {!rows.length && (
        <Card>
          <CardBody>
            <EmptyState
              title={emptyStateTitle}
              subtitle={formatMessage(
                {
                  id: 'app.views.administration.people_administration.administrators.admin_roles_assignment_table.subtitle.there_are_no_people_with_rolename_assigned_to_this_role_in_the_organization',
                  defaultMessage:
                    'There are no people with {roleName} assigned to this role in the organization.',
                },
                {
                  roleName: title,
                }
              )}
            >
              <AdminRoleAddOrEditButton
                buttonClassName="btn-primary"
                roleName={title}
                role={role}
                buttonText={formatMessage(
                  {
                    id: 'app.views.administration.people_administration.administrators.admin_roles_assignment_table.button_text',
                    defaultMessage: 'Add {roleName}',
                  },
                  {
                    roleName: title,
                  }
                )}
                title={formatMessage(
                  {
                    id: 'app.views.administration.people_administration.administrators.admin_roles_assignment_table.title',
                    defaultMessage: 'Add {roleName}',
                  },
                  {
                    roleName: title,
                  }
                )}
                callback={onItemChange}
              />
            </EmptyState>
          </CardBody>
        </Card>
      )}
      {rows.length > 0 && (
        <FilterablePeopleTable
          rows={rows}
          columns={columns}
          hideFilters={true}
          hideExportButton={true}
          initialTableSize={TALBE_SIZE_NO_LIMIT}
        />
      )}
    </>
  );
};

export default React.memo(AdminRolesAssignmentTable);
